import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/base/JsonLd";

interface SEOArticleProps {
  title: string;
  subtitle: string;
  heroImageUrl: string;
  heroImageAlt: string;
  children: React.ReactNode;
}

const siteUrl = import.meta.env.VITE_SITE_URL || "https://hipobuyspreadsheet.biz";

export default function SEOArticleLayout({ title, subtitle, heroImageUrl, heroImageAlt, children }: SEOArticleProps) {
  useEffect(() => {
    document.title = title + " | Hipobuy Spreadsheet";
    window.scrollTo(0, 0);
  }, [title]);

  const currentUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return siteUrl + window.location.pathname;
    }
    return siteUrl;
  }, []);

  const articleSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${currentUrl}#article`,
    headline: title,
    description: subtitle,
    image: heroImageUrl,
    inLanguage: "fr",
    author: {
      "@type": "Organization",
      name: "Hipobuy Spreadsheet",
      url: siteUrl,
    },
    publisher: {
      "@type": "Organization",
      name: "Hipobuy Spreadsheet",
      url: siteUrl,
    },
    datePublished: "2025-06-15",
    dateModified: "2026-06-23",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": currentUrl,
    },
  }), [title, subtitle, heroImageUrl, currentUrl]);

  const breadcrumbSchema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: siteUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: title,
        item: currentUrl,
      },
    ],
  }), [title, currentUrl]);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />
      <Navbar />
      <main>
        <article>
          <section className="relative flex flex-col lg:flex-row min-h-[40vh] lg:min-h-[50vh]">
            <div className="flex-1 flex flex-col justify-center px-4 md:px-8 lg:px-16 pt-24 pb-8 lg:py-0 bg-background-50 order-2 lg:order-1">
              <div className="max-w-xl">
                <Link to="/" className="inline-flex items-center gap-2 text-xs text-foreground-400 hover:text-foreground-600 transition-colors mb-5 whitespace-nowrap">
                  <i className="ri-arrow-left-line"></i>
                  Retour à l'accueil
                </Link>
                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight">
                  {title}
                </h1>
                <p className="mt-3 text-sm md:text-base text-foreground-500 leading-relaxed">
                  {subtitle}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-medium whitespace-nowrap">Tutoriel</span>
                  <span className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium whitespace-nowrap">Guide 2025</span>
                  <span className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap">E-commerce</span>
                </div>
              </div>
            </div>
            <div className="flex-1 relative min-h-[250px] lg:min-h-full bg-background-100 overflow-hidden order-1 lg:order-2">
              <img
                src={heroImageUrl}
                alt={heroImageAlt}
                title={title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </section>

          <section className="py-10 md:py-14 px-4 md:px-8 lg:px-16 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                <aside className="lg:w-56 flex-shrink-0">
                  <div className="sticky top-24 bg-background-100 rounded-2xl p-5">
                    <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Navigation</h4>
                    <nav className="flex flex-col gap-1.5">
                      <Link to="/guide-hipobuy-spreadsheet" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors whitespace-nowrap">
                        Guide complet
                      </Link>
                      <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 transition-colors underline underline-offset-4 whitespace-nowrap mt-3 pt-3 border-t border-background-200">
                        ← Retour à l'accueil
                      </Link>
                    </nav>
                  </div>
                </aside>
                <div className="flex-1 min-w-0 prose-custom">
                  {children}
                  <div className="mt-10 pt-8 border-t border-background-200">
                    <div className="flex flex-wrap gap-3">
                      <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 transition-colors underline underline-offset-4 whitespace-nowrap">
                        ← Page d'accueil Hipobuy Spreadsheet
                      </Link>
                      <Link to="/guide-hipobuy-spreadsheet" className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors underline underline-offset-4 whitespace-nowrap">
                        Guide complet
                      </Link>
                      <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors underline underline-offset-4 whitespace-nowrap">
                        Kaspreadsheet.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
}